import React, { useMemo, useState } from 'react';
import type { FC, PropsWithChildren } from 'react';
import PropTypes from 'prop-types';
import type { Site, SupportedLocale } from '@salesforce/pwa-kit-runtime/utils/ssr-config';

interface IMultiSiteContext {
    site: Site;
    locale: SupportedLocale;
    buildUrl: (path: string, siteRef?: string, localeRef?: string) => string;
}
/**
 * This is the global state for the multiples sites and locales supported in the App.
 *
 * To use the context simply import them into the component requiring context
 * like the below example:
 *
 * import React, {useContext} from 'react'
 * import {MultiSiteContext} from './contexts'
 *
 * export const RootCurrencyLabel = () => {
 *    const {site,locale,urlTemplate} = useContext(MultiSiteContext)
 *    return <div>{site} {locale}</div>
 * }
 *
 * @type {React.Context<unknown>}
 */
export const MultiSiteContext = React.createContext<IMultiSiteContext | null>(null);

export const MultiSiteProvider: FC<PropsWithChildren<IMultiSiteContext>> = ({
    site: initialSite,
    locale: initialLocale,
    buildUrl,
    children,
}) => {
    const [site] = useState(initialSite);
    const [locale] = useState(initialLocale);

    const state = useMemo(
        () => ({
            site,
            locale,
            buildUrl,
        }),
        [site, locale, buildUrl],
    );

    return <MultiSiteContext.Provider value={state}>{children}</MultiSiteContext.Provider>;
};

MultiSiteProvider.propTypes = {
    children: PropTypes.any.isRequired,
    buildUrl: PropTypes.func.isRequired,
    site: PropTypes.shape({
        id: PropTypes.string.isRequired,
        alias: PropTypes.string,
        l10n: PropTypes.shape({
            supportedCurrencies: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
            defaultCurrency: PropTypes.string.isRequired,
            defaultLocale: PropTypes.string.isRequired,
            supportedLocales: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    alias: PropTypes.string,
                    preferredCurrency: PropTypes.string.isRequired,
                }).isRequired,
            ).isRequired,
        }).isRequired,
    }).isRequired,
    locale: PropTypes.shape({
        id: PropTypes.string.isRequired,
        alias: PropTypes.string,
        preferredCurrency: PropTypes.string.isRequired,
    }).isRequired,
};
